import { onMount, onCleanup } from 'solid-js';

/**
 * Animated isometric-box background rendered on a `<canvas>`.
 *
 * Boxes float, drift, rotate gently, and pulse in/out of visibility.
 * Designed to overlay on a brown/amber gradient. Used across all public pages
 * (auth, landing, marketing) as the single shared background component.
 *
 * Props:
 * - `count`  — number of boxes (default 28)
 * - `speed`  — animation speed multiplier (default 1)
 * - `class`  — CSS classes on the canvas element
 */

interface IsometricBgProps {
  count?: number;
  speed?: number;
  /** RGB color of the boxes e.g. [255,255,255] for white (default) or [146,64,14] for amber */
  color?: [number, number, number];
  class?: string;
}

/* ── Box state ──────────────────────────────────────────────────────── */

interface Box {
  x: number;
  y: number;
  w: number; // iso half-width
  d: number; // box depth
  angle: number; // rotation (radians)
  angleDrift: number;
  opacity: number;
  opacityMin: number;
  opacityMax: number;
  fadeDir: number;
  fadeSpeed: number;
  vx: number;
  vy: number;
  strokeAlpha: number;
  layer: number; // 0=far (small/faint) 1=mid 2=near (big/bright)
}

/* ── Helpers ────────────────────────────────────────────────────────── */

function rand(lo: number, hi: number) {
  return lo + Math.random() * (hi - lo);
}

function createBox(cw: number, ch: number): Box {
  // 3 depth layers with different size/opacity ranges
  const layerRoll = Math.random();
  const layer = layerRoll < 0.35 ? 0 : layerRoll < 0.75 ? 1 : 2;

  const sizeRanges = [
    [18, 36], // far — tiny
    [34, 62], // mid
    [58, 96], // near — large
  ];
  const opacityRanges = [
    [0.03, 0.08],
    [0.06, 0.14],
    [0.1, 0.2],
  ];
  const strokeRanges = [
    [0.12, 0.22],
    [0.22, 0.36],
    [0.32, 0.48],
  ];
  const speedScale = [0.35, 0.65, 1.0];

  const [sMin, sMax] = sizeRanges[layer] as [number, number];
  const [oMin, oMax] = opacityRanges[layer] as [number, number];
  const [stMin, stMax] = strokeRanges[layer] as [number, number];
  const sp = speedScale[layer] as number;

  const w = rand(sMin, sMax);
  return {
    x: rand(-100, cw + 100),
    y: rand(-100, ch + 100),
    w,
    d: w * rand(0.7, 1.3),
    angle: rand(-0.35, 0.35),
    angleDrift: rand(-0.0008, 0.0008) * sp,
    opacity: rand(oMin, oMax),
    opacityMin: oMin,
    opacityMax: oMax,
    fadeDir: Math.random() > 0.5 ? 1 : -1,
    fadeSpeed: rand(0.0004, 0.0015),
    vx: rand(-0.25, 0.25) * sp,
    vy: rand(-0.18, 0.18) * sp,
    strokeAlpha: rand(stMin, stMax),
    layer,
  };
}

/* ── Drawing ────────────────────────────────────────────────────────── */

/** Rotate a point (px, py) around origin by angle a */
function rot(px: number, py: number, a: number): [number, number] {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return [px * c - py * s, px * s + py * c];
}

function drawBox(
  ctx: CanvasRenderingContext2D,
  box: Box,
  rgb: [number, number, number]
) {
  const { x, y, w, d, angle, opacity, strokeAlpha } = box;
  const [r, g, b] = rgb;
  const h = w * 0.5; // iso half-height of top face

  // Compute 4 top-face vertices individually (rotated)
  const t0 = rot(0, -h, angle); // top
  const t1 = rot(w, 0, angle); // right
  const t2 = rot(0, h, angle); // bottom
  const t3 = rot(-w, 0, angle); // left

  // Bottom-face = top face shifted down by d
  const b1: [number, number] = [t1[0], t1[1] + d];
  const b2: [number, number] = [t2[0], t2[1] + d];
  const b3: [number, number] = [t3[0], t3[1] + d];

  // ── Fill faces ──

  // Top face (brightest)
  ctx.fillStyle = `rgba(${r},${g},${b},${opacity})`;
  ctx.beginPath();
  ctx.moveTo(x + t0[0], y + t0[1]);
  ctx.lineTo(x + t1[0], y + t1[1]);
  ctx.lineTo(x + t2[0], y + t2[1]);
  ctx.lineTo(x + t3[0], y + t3[1]);
  ctx.closePath();
  ctx.fill();

  // Left face (darkest)
  ctx.fillStyle = `rgba(${r},${g},${b},${opacity * 0.5})`;
  ctx.beginPath();
  ctx.moveTo(x + t3[0], y + t3[1]);
  ctx.lineTo(x + t2[0], y + t2[1]);
  ctx.lineTo(x + b2[0], y + b2[1]);
  ctx.lineTo(x + b3[0], y + b3[1]);
  ctx.closePath();
  ctx.fill();

  // Right face (mid)
  ctx.fillStyle = `rgba(${r},${g},${b},${opacity * 0.7})`;
  ctx.beginPath();
  ctx.moveTo(x + t1[0], y + t1[1]);
  ctx.lineTo(x + t2[0], y + t2[1]);
  ctx.lineTo(x + b2[0], y + b2[1]);
  ctx.lineTo(x + b1[0], y + b1[1]);
  ctx.closePath();
  ctx.fill();

  // ── Wireframe edges ──
  ctx.strokeStyle = `rgba(${r},${g},${b},${strokeAlpha})`;
  ctx.lineWidth = box.layer === 0 ? 0.5 : box.layer === 1 ? 0.7 : 0.9;

  // Top face outline
  ctx.beginPath();
  ctx.moveTo(x + t0[0], y + t0[1]);
  ctx.lineTo(x + t1[0], y + t1[1]);
  ctx.lineTo(x + t2[0], y + t2[1]);
  ctx.lineTo(x + t3[0], y + t3[1]);
  ctx.closePath();
  ctx.stroke();

  // Left face outline
  ctx.beginPath();
  ctx.moveTo(x + t3[0], y + t3[1]);
  ctx.lineTo(x + t2[0], y + t2[1]);
  ctx.lineTo(x + b2[0], y + b2[1]);
  ctx.lineTo(x + b3[0], y + b3[1]);
  ctx.closePath();
  ctx.stroke();

  // Right face outline
  ctx.beginPath();
  ctx.moveTo(x + t1[0], y + t1[1]);
  ctx.lineTo(x + t2[0], y + t2[1]);
  ctx.lineTo(x + b2[0], y + b2[1]);
  ctx.lineTo(x + b1[0], y + b1[1]);
  ctx.closePath();
  ctx.stroke();
}

/* ── Component ──────────────────────────────────────────────────────── */

export function IsometricBg(props: IsometricBgProps) {
  let canvas: HTMLCanvasElement | undefined;

  onMount(() => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const speed = props.speed ?? 1;
    const count = props.count ?? 28;
    const color = props.color ?? ([255, 255, 255] as [number, number, number]);

    // Size canvas to its container
    let cw = 0;
    let ch = 0;
    function resize() {
      if (!canvas) return;
      const rect =
        canvas.parentElement?.getBoundingClientRect() ??
        canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      cw = rect.width;
      ch = rect.height;
      canvas.width = cw * dpr;
      canvas.height = ch * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(canvas.parentElement ?? canvas);

    // Create boxes
    const boxes: Box[] = Array.from({ length: count }, () => createBox(cw, ch));

    let raf = 0;

    function tick() {
      if (!ctx) return;
      ctx.clearRect(0, 0, cw, ch);

      // Sort by layer so far boxes render behind near boxes
      boxes.sort((a, b) => a.layer - b.layer);

      for (const box of boxes) {
        // Drift
        box.x += box.vx * speed;
        box.y += box.vy * speed;

        // Gentle rotation
        box.angle += box.angleDrift * speed;

        // Opacity pulse
        box.opacity += box.fadeDir * box.fadeSpeed * speed;
        if (box.opacity >= box.opacityMax) {
          box.opacity = box.opacityMax;
          box.fadeDir = -1;
        } else if (box.opacity <= box.opacityMin) {
          box.opacity = box.opacityMin;
          box.fadeDir = 1;
        }

        // Wrap around edges (with generous padding)
        const pad = box.w + box.d + 40;
        if (box.x < -pad) box.x = cw + pad * 0.5;
        else if (box.x > cw + pad) box.x = -pad * 0.5;
        if (box.y < -pad) box.y = ch + pad * 0.5;
        else if (box.y > ch + pad) box.y = -pad * 0.5;

        drawBox(ctx, box, color);
      }

      raf = requestAnimationFrame(tick);
    }

    raf = requestAnimationFrame(tick);

    onCleanup(() => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    });
  });

  return (
    <canvas
      ref={canvas}
      class={props.class}
      aria-hidden="true"
      style={{ 'pointer-events': 'none' }}
    />
  );
}
